/**
 * 値が空文字列になっているpropertyを再帰的にundefinedに変換する
 * NOT TYPE SAFE: すべてのpropertyがundefinedに置き換わっても問題ないということを仮定しているので注意する
 */
export function replaceEmptyStringWithUndefined<T>(data: T): T {
  if (data === "") {
    // 空文字を undefined に置き換える
    return undefined as T;
  }

  if (Array.isArray(data)) {
    // 配列の場合は再帰的に処理
    return data.map(replaceEmptyStringWithUndefined) as T;
  }

  if (data !== null && typeof data === "object") {
    // オブジェクトの場合も再帰的に処理
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        key,
        replaceEmptyStringWithUndefined(value),
      ]),
    ) as T;
  }
  return data as T;
}

/**
 * 値がnullになっているpropertyを再帰的にundefinedに変換する
 */
type ReplaceNullWithUndefined<T> =
  // ① T が null の場合は undefined
  T extends null
    ? undefined
    : // ② T が配列型の場合は要素に対して再帰適用
      T extends Array<infer U>
      ? Array<ReplaceNullWithUndefined<U>>
      : // ③ T がオブジェクト(クラスも含む)の場合は全プロパティに対して再帰適用
        T extends object
        ? { [K in keyof T]: ReplaceNullWithUndefined<T[K]> }
        : // ④ それ以外の型はそのまま
          T;
export function replaceNullWithUndefined<T>(
  data: T,
): ReplaceNullWithUndefined<T> {
  if (data === null) {
    // null を undefined に置き換える
    return undefined as ReplaceNullWithUndefined<T>;
  }

  if (Array.isArray(data)) {
    // 配列の場合は再帰的に処理
    return data.map(replaceNullWithUndefined) as ReplaceNullWithUndefined<T>;
  }

  if (data !== null && typeof data === "object") {
    // オブジェクトの場合も再帰的に処理
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        key,
        replaceNullWithUndefined(value),
      ]),
    ) as ReplaceNullWithUndefined<T>;
  }
  return data as ReplaceNullWithUndefined<T>;
}

export const entries = <T extends object = object>(
  obj: T,
): [keyof T, T[keyof T]][] => {
  return Object.entries(obj) as [keyof T, T[keyof T]][];
};
